"use client";

import { motion, useReducedMotion } from "framer-motion";
import { Section } from "@/components/ui/Section";
import { Eyebrow } from "@/components/ui/Eyebrow";
import { SectionTitle } from "@/components/ui/SectionTitle";
import { ease } from "@/lib/motion/easings";
import { cn } from "@/lib/utils/cn";
import type { RoadmapPhase as Phase } from "@/lib/sanity";

function StatusPill({ phase }: { phase: Phase }) {
  const current = phase.status === "current";
  if (current) {
    return (
      <span className="inline-flex items-center gap-2 rounded-sm border border-crimson px-3 py-1 font-display text-[10px] uppercase tracking-widest text-crimson">
        <span aria-hidden className="size-1.5 animate-pulse rounded-full bg-crimson" />
        {phase.targetingLabel}
      </span>
    );
  }
  return (
    <span
      className={cn(
        "inline-block rounded-sm border px-3 py-1 font-display text-[10px] uppercase tracking-widest",
        phase.status === "upcoming" ? "border-gold/50 text-gold" : "border-line text-fg-faint",
      )}
    >
      {phase.targetingLabel}
    </span>
  );
}

function PhasePanel({ phase }: { phase: Phase }) {
  const current = phase.status === "current";
  return (
    <div
      className={cn(
        "relative overflow-hidden rounded-lg border bg-base/40 p-7 lg:p-8",
        current
          ? "border-line-gold shadow-[0_0_50px_-12px_rgba(207,174,96,0.4)]"
          : "border-line",
      )}
    >
      {current && (
        <div
          aria-hidden
          className="pointer-events-none absolute -right-1/4 -top-1/3 h-2/3 w-2/3"
          style={{
            background:
              "radial-gradient(ellipse 60% 60% at 70% 30%, rgba(207,174,96,0.12), transparent 70%)",
          }}
        />
      )}
      <div className="relative flex items-center justify-between gap-4">
        <div className="flex items-baseline gap-3">
          <span className="font-display text-5xl tabular-nums tracking-tight text-gold lg:text-6xl">
            {String(phase.number).padStart(2, "0")}
          </span>
          <span className="font-display text-[10px] uppercase tracking-[0.3em] text-gold-dark">
            {phase.act}
          </span>
        </div>
        <StatusPill phase={phase} />
      </div>

      <h3 className="relative mt-5 font-display text-3xl uppercase tracking-tight text-fg lg:text-4xl">
        {phase.title}
      </h3>
      {phase.tagline && (
        <p className="relative mt-2 font-body text-[1rem] italic text-gold lg:text-lg">
          {phase.tagline}
        </p>
      )}
      {phase.summary && (
        <p className="relative mt-4 font-body text-sm leading-relaxed text-fg-muted">
          {phase.summary}
        </p>
      )}
      {phase.bullets?.length > 0 && (
        <ul className="relative mt-6 grid grid-cols-1 gap-x-6 gap-y-2 sm:grid-cols-2">
          {phase.bullets.map((b) => (
            <li key={b} className="flex items-start gap-2 font-body text-sm text-fg-muted">
              <span aria-hidden className="mt-[7px] h-1 w-1 shrink-0 rounded-full bg-gold" />
              {b}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export function RoadmapJourney({ phases }: { phases: Phase[] }) {
  const reduced = useReducedMotion();
  if (!phases?.length) return null;

  return (
    <Section tone="elevated" divided aria-labelledby="roadmap-journey-title">
      <div className="max-w-3xl">
        <Eyebrow>The phases</Eyebrow>
        <SectionTitle id="roadmap-journey-title" accent="to League Play." className="mt-4">
          From Genesis
        </SectionTitle>
        <p className="mt-6 font-body text-lg leading-relaxed text-fg-muted">
          No pay-to-win shortcut to a finished product, each act ships, the
          format hardens in public, and the league grows underneath it.
        </p>
      </div>

      <div className="relative mx-auto mt-16 max-w-5xl lg:mt-20">
        {/* rail: left on mobile, centre on desktop */}
        <span
          aria-hidden
          className="pointer-events-none absolute bottom-0 top-0 left-[7px] w-px bg-gradient-to-b from-gold/50 via-line-gold to-line lg:left-1/2 lg:-translate-x-1/2"
        />

        <div className="space-y-10 lg:space-y-16">
          {phases.map((phase, i) => {
            const leftSide = i % 2 === 0;
            const current = phase.status === "current";
            return (
              <div key={phase._id} className="relative lg:grid lg:grid-cols-2 lg:gap-x-16">
                {/* node */}
                <span
                  aria-hidden
                  className="absolute top-7 left-[7px] z-10 -translate-x-1/2 lg:left-1/2"
                >
                  <span className="relative flex h-4 w-4 items-center justify-center">
                    {current && (
                      <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-gold opacity-50" />
                    )}
                    <span
                      className={cn(
                        "relative inline-flex h-3.5 w-3.5 rounded-full border-2 bg-base",
                        current
                          ? "border-gold bg-gold shadow-[0_0_16px_rgba(207,174,96,0.6)]"
                          : phase.status === "upcoming"
                            ? "border-gold/60"
                            : "border-line-gold",
                      )}
                    />
                  </span>
                </span>

                {/* panel — alternates sides on desktop */}
                <motion.article
                  initial={reduced ? false : { opacity: 0, y: 26 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "0px 0px -12% 0px" }}
                  transition={{ duration: 0.6, ease: ease.cinematic }}
                  className={cn(
                    "ml-9 lg:ml-0",
                    leftSide ? "lg:col-start-1 lg:pr-4" : "lg:col-start-2 lg:pl-4",
                  )}
                >
                  <PhasePanel phase={phase} />
                </motion.article>
              </div>
            );
          })}
        </div>
      </div>
    </Section>
  );
}
